import { useState, useEffect, useCallback } from "react";
import { Loader2, LogOut, Download, RefreshCw, CheckCircle2, Circle, XCircle, Mail, Phone, CalendarDays, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_COLOURS = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  confirmed: "bg-blue-100 text-blue-800",
  cancelled: "bg-slate-200 text-slate-600",
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/admin/login", { email, password });
      onLogin();
      toast.success("Welcome back");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1B3A] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-3xl p-10 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.jpg" alt="logo" className="w-12 h-12 rounded-full object-cover" />
          <div>
            <div className="font-display uppercase text-xl text-slate-900">Advanced <span className="text-[#1E3A8A]">Coaching</span></div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Admin · Sign in</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="eyebrow text-slate-500">Email</label>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-[#1E3A8A] outline-none"
              autoFocus
            />
          </div>
          <div>
            <label className="eyebrow text-slate-500">Password</label>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-[#1E3A8A] outline-none"
            />
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="btn-gold w-full justify-center">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = "blue" }) {
  const color = accent === "gold" ? "text-[#F59E0B]" : accent === "emerald" ? "text-emerald-600" : "text-[#1E3A8A]";
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="eyebrow text-slate-500">{label}</div>
      <div className={`font-display text-3xl mt-2 ${color}`}>{value}</div>
    </div>
  );
}

function BookingRow({ b, onUpdate }) {
  const [updating, setUpdating] = useState(false);
  const change = async (newStatus) => {
    setUpdating(true);
    try {
      await apiClient.patch(`/admin/bookings/${b.id}`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      onUpdate();
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setUpdating(false);
    }
  };
  const days = (b.selected_days || []).join(", ");
  const optionLabel = b.booking_option === "full_week" ? "Full week (Mon–Thu)" : b.booking_option === "daily" ? `Daily: ${days || "—"}` : "—";
  const campDates = (b.camp_start_date && b.camp_end_date) ? `${b.camp_start_date} → ${b.camp_end_date}` : null;
  return (
    <div data-testid={`booking-row-${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-display uppercase text-xl text-slate-900">{b.parent_name}</h3>
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${STATUS_COLOURS[b.status] || "bg-slate-100 text-slate-600"}`}>{b.status}</span>
          </div>
          <div className="text-sm text-slate-500 mt-1">Child: {b.child_name} (age {b.child_age}){b.num_children > 1 ? ` · +${b.num_children - 1} sibling${b.num_children > 2 ? "s" : ""}` : ""}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[#1E3A8A]">{b.total_amount ? `£${Number(b.total_amount).toFixed(2)}` : "—"}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{b.payment_method === "online" ? "Pay online" : "Pay at session"}</div>
        </div>
      </div>

      {b.camp_title && (
        <div className="mt-3 p-3 rounded-lg bg-[#1E3A8A]/5 border border-[#1E3A8A]/15">
          <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-bold">Camp booked</div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5">{b.camp_title}</div>
          {campDates && <div className="text-xs text-slate-600 mt-1 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-[#1E3A8A]" />{campDates}</div>}
          <div className="text-xs text-slate-600 mt-1">Attending: <strong>{optionLabel}</strong></div>
        </div>
      )}

      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-slate-600">
        <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#1E3A8A]" /><a className="hover:text-[#1E3A8A]" href={`mailto:${b.email}`}>{b.email}</a></div>
        <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#1E3A8A]" /><a className="hover:text-[#1E3A8A]" href={`tel:${b.phone}`}>{b.phone}</a></div>
        {!b.camp_title && <div className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-[#1E3A8A]" />{optionLabel}</div>}
        <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" />{b.camp_id || b.service_type.replace(/_/g, " ")}</div>
      </div>

      {(b.notes || b.additional_children) && (
        <div className="mt-3 text-xs text-slate-500 italic">
          {b.additional_children && <div>Siblings: {b.additional_children}</div>}
          {b.notes && <div>Notes: {b.notes}</div>}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid={`mark-paid-${b.id}`} disabled={updating || b.status === "paid"} onClick={() => change("paid")} className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white font-semibold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3" />Paid</button>
        <button data-testid={`mark-confirmed-${b.id}`} disabled={updating || b.status === "confirmed"} onClick={() => change("confirmed")} className="text-xs px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white font-semibold uppercase tracking-wider hover:bg-[#1E40AF] disabled:opacity-50 inline-flex items-center gap-1.5"><Circle className="w-3 h-3" />Confirmed</button>
        <button data-testid={`mark-cancelled-${b.id}`} disabled={updating || b.status === "cancelled"} onClick={() => change("cancelled")} className="text-xs px-3 py-1.5 rounded-full bg-slate-200 text-slate-700 font-semibold uppercase tracking-wider hover:bg-slate-300 disabled:opacity-50 inline-flex items-center gap-1.5"><XCircle className="w-3 h-3" />Cancel</button>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, s] = await Promise.all([
        apiClient.get(`/admin/bookings${filter !== "all" ? `?status_filter=${filter}` : ""}`),
        apiClient.get("/admin/contact"),
        apiClient.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setContacts(c.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [filter, onLogout]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    try {
      const res = await apiClient.get("/admin/bookings/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bookings-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const signOut = async () => {
    try { await apiClient.post("/admin/logout"); } catch (err) { console.warn("Logout request failed (clearing local state anyway):", err); }
    onLogout();
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-100">
      <header className="bg-[#0B1B3A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="logo" className="w-10 h-10 rounded-full object-cover" />
            <div>
              <div className="font-display uppercase text-lg">Advanced <span className="text-[#FBBF24]">Coaching</span></div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Admin Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="px-3 py-2 rounded-full border border-white/15 text-xs uppercase tracking-widest font-semibold hover:bg-white/10 inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" />Refresh</button>
            <button data-testid="admin-export-csv" onClick={exportCSV} className="px-3 py-2 rounded-full bg-[#FBBF24] text-[#0B1B3A] text-xs uppercase tracking-widest font-bold hover:bg-[#F59E0B] inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />Export CSV</button>
            <button data-testid="admin-logout" onClick={signOut} className="px-3 py-2 rounded-full border border-white/15 text-xs uppercase tracking-widest font-semibold hover:bg-white/10 inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" />Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            <StatCard label="Total" value={stats.total_bookings} />
            <StatCard label="Pending" value={stats.pending} />
            <StatCard label="Paid" value={stats.paid} accent="emerald" />
            <StatCard label="Confirmed" value={stats.confirmed} />
            <StatCard label="Cancelled" value={stats.cancelled} />
            <StatCard label="Contacts" value={stats.contacts} />
            <StatCard label="Revenue" value={`£${stats.revenue.toFixed(2)}`} accent="gold" />
          </div>
        )}

        <div className="flex items-center gap-2 mb-4 border-b border-slate-200">
          <button data-testid="tab-bookings" onClick={() => setTab("bookings")} className={`px-4 py-2 text-sm font-display uppercase tracking-wider border-b-2 -mb-px transition ${tab === "bookings" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>Bookings ({bookings.length})</button>
          <button data-testid="tab-contacts" onClick={() => setTab("contacts")} className={`px-4 py-2 text-sm font-display uppercase tracking-wider border-b-2 -mb-px transition ${tab === "contacts" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>Messages ({contacts.length})</button>
        </div>

        {tab === "bookings" && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  data-testid={`filter-${s.value}`}
                  onClick={() => setFilter(s.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest border transition ${filter === s.value ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#1E3A8A]/40"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
            ) : bookings.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">No bookings to show.</div>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => <BookingRow key={b.id} b={b} onUpdate={load} />)}
              </div>
            )}
          </>
        )}

        {tab === "contacts" && (
          loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
          ) : contacts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">No messages.</div>
          ) : (
            <div className="space-y-3">
              {contacts.map((m) => (
                <div key={m.id} data-testid={`contact-row-${m.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-4 h-4 text-[#1E3A8A]" />
                      <div className="font-display uppercase text-lg">{m.name}</div>
                      {m.subject && <span className="text-xs text-slate-500">· {m.subject}</span>}
                    </div>
                    <div className="text-xs text-slate-500">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="mt-1 text-xs text-slate-600 flex gap-3 flex-wrap">
                    <a href={`mailto:${m.email}`} className="hover:text-[#1E3A8A]">{m.email}</a>
                    {m.phone && <a href={`tel:${m.phone}`} className="hover:text-[#1E3A8A]">· {m.phone}</a>}
                  </div>
                  <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{m.message}</p>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  // null = unknown, true = authed, false = not authed
  const [authed, setAuthed] = useState(null);

  const verify = useCallback(async () => {
    try {
      await apiClient.get("/admin/me");
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0B1B3A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#FBBF24]" />
      </div>
    );
  }
  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
}
